// Fetch de plantillas (templates + categorías) desde Supabase para
// Interfaz V2. Mapea las filas crudas al shape que la UI ya consume
// (CollectionV2 / PresetV2) — así el resto de los componentes no
// cambian, solo el origen de los datos.
//
// Reglas de filtrado:
//   • Solo plantillas activas (`activo = true`).
//   • Solo de tipo imagen (`output_type IN 'imagen', 'imagen_pro'`).
//     La UI actual no soporta video; cuando se agregue, levantar este filtro.
//   • Globales (`id_company IS NULL`) + las de la empresa del user.
//   • Por rubro: si el template tiene `rubros = '{}'` se considera "para
//     todos los rubros"; sino, debe contener el rubro del user. El rubro
//     ORDENA, no excluye: ver `MIN_CATEGORIAS` abajo.
//   • Categorías vacías (sin templates que pasen los filtros) NO se
//     renderizan en la home — no tendría sentido mostrar una fila vacía.
//   • Las plantillas de un modo con catálogo CURADO quedan afuera siempre, pasen o
//     no el filtro de rubro. Hoy son las de Belleza, y la pregunta es
//     `esPlantillaDeBelleza`: su categoría es de Belleza (lista curada + cualquier
//     slug `*-belleza`) o su `rubros` declara el rubro. Lo segundo es lo que evita
//     que una plantilla de Belleza cargada en una categoría de Moda se cuele acá
//     — ADR-2026-09-11-la-plantilla-va-al-catalogo-de-su-rubro.

import { supabase } from '../../lib/supabase';
import { SUPABASE_URL } from '../../config';
import type { CollectionV2, PresetV2 } from './mockData';
import { esCategoriaDeBelleza, esPlantillaDeBelleza } from './categoriasCuradas';

const TEMPLATES_BUCKET = 'templates';

/**
 * Cuántas categorías tiene que juntar el filtro por rubro antes de que valga la
 * pena mostrarlas solas — ADR-2026-08-03-plantillas-rubro-ordena-no-excluye.
 *
 * Es la cantidad de filas que la home pinta (`useRotacionPlantillas(…, 3)`). Hoy
 * las 182 plantillas activas tienen `rubros = {'Moda e Indumentaria'}`, así que
 * con el filtro estricto CUALQUIER otro rubro se quedaba con cero categorías: la
 * sección "Plantillas" mostraba una sola fila, "Fondo blanco", que son las poses
 * de e-commerce y no pasan por acá.
 *
 * Mismo criterio que ya se le había aplicado a la fila de modos, des-gateada de
 * `DESIGN_RUBROS` porque "cualquier otro rubro veía la home vacía": el rubro es
 * una señal de relevancia (va primero), no un permiso.
 */
const MIN_CATEGORIAS = 3;

/**
 * Tope de la descripción de una categoría — ADR-2026-08-03-descripciones-plantillas-100-y-fuera-en-mobile.
 *
 * Las descripciones las escribe quien carga la categoría y no tienen límite en la
 * base: la de "Indie" son 121 caracteres y en la tarjeta de la home ocupa un renglón
 * entero, más largo que el título de la sección. El corte va acá y no en el CSS
 * (`line-clamp`) para que el tope sea uno solo y no dependa del ancho de cada
 * pantalla que la muestre.
 */
const MAX_DESCRIPCION = 100;

/**
 * Recorta a `MAX_DESCRIPCION`, **sin puntos suspensivos** — supersede el recorte con
 * "…" de ADR-2026-08-03-descripciones-plantillas-100-y-fuera-en-mobile.
 *
 * Ahora es una RED DE SEGURIDAD, no el mecanismo: las seis descripciones que pasaban
 * de 100 se reescribieron resumidas en la base (ADR-2026-08-03-descripciones-plantillas-resumidas-sin-ellipsis),
 * así que en el estado normal esta función no corta nada. El "…" se sacó porque
 * anunciaba un texto recortado justo cuando ya no lo está; si una descripción nueva
 * vuelve a pasarse, el corte limpio en la última palabra molesta menos que el aviso
 * —y el arreglo real es reescribir esa descripción, no alargar el tope—.
 */
function recortar(texto: string): string {
  const limpio = texto.trim();
  if (limpio.length <= MAX_DESCRIPCION) return limpio;
  const crudo = limpio.slice(0, MAX_DESCRIPCION);
  const espacio = crudo.lastIndexOf(' ');
  // El umbral evita que un texto sin espacios cerca del corte quede en dos palabras.
  const cortado = espacio > MAX_DESCRIPCION / 2 ? crudo.slice(0, espacio) : crudo;
  // Sin la puntuación colgando: "sobria," o "sobria:" se leen como error.
  return cortado.replace(/[\s.,;:—-]+$/, '');
}

/** Convierte un path del bucket en URL pública completa. */
export const templatePreviewUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  return `${SUPABASE_URL}/storage/v1/object/public/${TEMPLATES_BUCKET}/${path}`;
};

export interface FetchTemplatesArgs {
  /** Rubro del user (de `company_profiles.rubro`). null = sin rubro definido. */
  rubro: string | null;
  /** id_company del user. null = sin empresa (solo se ven globales). */
  companyId: string | null;
}

export interface RawTemplate {
  id: string;
  categoria_id: string;
  nombre: string;
  descripcion: string | null;
  preview_opt_path: string | null;
  preview_full_path: string | null;
  output_type: string;
  rubros: string[] | null;
  tipos_producto_sugeridos: string[] | null;
  inputs_aceptados: string[] | null;
  creditos_por_unidad: number | string | null;
  unidad_consumo: string | null;
  duration_seconds: number | string | null;
  id_company: string | null;
  orden: number;
}

export interface RawCategoria {
  id: string;
  nombre: string;
  slug: string;
  descripcion: string | null;
  orden: number;
}

/**
 * Columnas de `templates` que la UI necesita — una sola lista para que el fetch de
 * Moda y el de Belleza (`templatesBellezaApi`) no se desincronicen: agregar una
 * columna acá la deja disponible en las dos pantallas de una.
 */
export const COLUMNAS_TEMPLATE =
  'id, categoria_id, nombre, descripcion, preview_opt_path, preview_full_path, output_type, rubros, tipos_producto_sugeridos, inputs_aceptados, creditos_por_unidad, unidad_consumo, duration_seconds, id_company, orden';

/**
 * Una categoría + sus plantillas, en el shape que consume la UI.
 *
 * Vive afuera de `fetchTemplatesV2` porque lo usa también el fetch de las
 * plantillas de Belleza (ADR-2026-09-10-plantillas-belleza-modo-propio): las dos
 * pantallas pueden diverger en lo que muestran, pero una fila de `templates` se
 * traduce a un `PresetV2` de una sola manera y no hay motivo para tener dos.
 */
export function mapearColeccion(
  cat: RawCategoria,
  tpls: RawTemplate[],
): CollectionV2 {
  return {
    id: cat.slug, // usar slug para URLs
    title: cat.nombre,
    subtitle: cat.descripcion ? recortar(cat.descripcion) : undefined,
    presets: tpls.map(
      (t): PresetV2 => ({
        id: t.id,
        title: t.nombre,
        description: t.descripcion || undefined,
        // opt → thumbnails (home grids, carruseles); full → previews
        // grandes (sheet, "Utilizar" carousel). Si full falta, los
        // componentes fallback a imageUrl (opt).
        imageUrl: templatePreviewUrl(t.preview_opt_path),
        imageUrlFull: t.preview_full_path
          ? templatePreviewUrl(t.preview_full_path)
          : undefined,
        kind: 'template',
        tiposProductoSugeridos: Array.isArray(t.tipos_producto_sugeridos)
          ? t.tipos_producto_sugeridos
          : undefined,
        inputsAceptados: Array.isArray(t.inputs_aceptados)
          ? t.inputs_aceptados
          : undefined,
        outputType:
          t.output_type === 'video'
            ? 'video'
            : t.output_type === 'imagen_pro'
              ? 'imagen_pro'
              : 'imagen',
        creditosPorUnidad:
          t.creditos_por_unidad != null
            ? Number(t.creditos_por_unidad)
            : undefined,
        unidadConsumo:
          t.unidad_consumo === 'segundo'
            ? 'segundo'
            : t.unidad_consumo === 'imagen'
              ? 'imagen'
              : t.unidad_consumo === 'generacion'
                ? 'generacion'
                : undefined,
        durationSeconds:
          t.duration_seconds != null ? Number(t.duration_seconds) : undefined,
      }),
    ),
  };
}

/**
 * Devuelve las plantillas agrupadas por categoría, listas para renderizar
 * como CollectionV2[]. El `id` de cada colección es el `slug` de la
 * categoría — usado en la URL `/interfaz-v2/coleccion/:slug`.
 */
export async function fetchTemplatesV2({
  rubro,
  companyId,
}: FetchTemplatesArgs): Promise<CollectionV2[]> {
  // 1. Categorías activas, ordenadas.
  const { data: categoriasRaw, error: catErr } = await supabase
    .from('template_categorias')
    .select('id, nombre, slug, descripcion, orden')
    .eq('activo', true)
    .order('orden', { ascending: true });
  if (catErr || !categoriasRaw) {
    console.error('[templatesApi] error fetching categorias', catErr);
    return [];
  }
  // Las categorías de un modo con catálogo curado no entran acá NUNCA, ni por rubro
  // ni por relleno — ADR-2026-09-10-el-catalogo-de-moda-no-toma-categorias-curadas.
  //
  // No alcanzaba con que sus plantillas tengan `rubros = {'Belleza y Cuidado
  // Personal'}`, por dos caminos que se suman:
  //
  //  • El `rubro` que llega acá es el de la EMPRESA, no el de la marca activa (así lo
  //    resuelven los tres llamadores). Una empresa de Belleza con una marca de Moda
  //    abre la pantalla de Moda —el gate mira la marca— pero filtra el catálogo con
  //    "Belleza": las dos categorías de skincare pasaban el filtro.
  //  • Y al ser sólo 2, quedaban debajo de `MIN_CATEGORIAS` y disparaban el relleno,
  //    que agrega TODAS las demás. Resultado: la pantalla de Moda abría con las dos
  //    filas de skincare y las diez de moda detrás.
  //
  // El corte va acá, en la fuente del catálogo, y no en el gate de rubro de cada
  // llamador: son tres pantallas (home, `/plantillas`, detalle de colección) y las
  // tres consumen esta función.
  //
  // El corte NO es una lista de slugs sino la pregunta `esCategoriaDeBelleza`, que
  // además del par curado toma cualquier slug terminado en `-belleza`
  // (ADR-2026-09-10-una-categoria-es-de-belleza-por-su-slug): cargar una categoría
  // nueva de Belleza no tiene que obligar a editar el repo para que Moda no la vea.
  const categorias = (categoriasRaw as RawCategoria[]).filter(
    (c) => !esCategoriaDeBelleza(c.slug),
  );
  // Y el slug de cada categoría a mano, para poder preguntarle a una plantilla si es
  // de Belleza: la pregunta mira su rubro Y el slug de la categoría donde vive.
  const slugPorCategoria = new Map(
    (categoriasRaw as RawCategoria[]).map((c) => [c.id, c.slug] as const),
  );

  // 2. Templates: activos + tipo imagen + visibles para el user.
  // Usamos `.or()` solo si companyId existe; sino, solo globales.
  let templatesQuery = supabase
    .from('templates')
    .select(COLUMNAS_TEMPLATE)
    .eq('activo', true)
    .in('output_type', ['imagen', 'imagen_pro', 'video'])
    .order('orden', { ascending: true });
  if (companyId) {
    templatesQuery = templatesQuery.or(
      `id_company.is.null,id_company.eq.${companyId}`,
    );
  } else {
    templatesQuery = templatesQuery.is('id_company', null);
  }
  const { data: templatesRaw, error: tplErr } = await templatesQuery;
  if (tplErr || !templatesRaw) {
    console.error('[templatesApi] error fetching templates', tplErr);
    return [];
  }
  // Fuera TODA plantilla de Belleza, por su rubro o por su categoría —
  // ADR-2026-09-11-la-plantilla-va-al-catalogo-de-su-rubro.
  //
  // El corte es por plantilla y no por categoría porque las dos cosas hacen falta:
  //  • `todas` —de donde sale el relleno— agrupa por `categoria_id` sin mirar la lista
  //    de categorías, así que las filas de una categoría `-belleza` seguían disponibles
  //    para el relleno por su id;
  //  • y una plantilla de Belleza cargada en una categoría de Moda no la tapa ningún
  //    filtro de categorías: la única señal que la delata es su `rubros`.
  const templates = (templatesRaw as RawTemplate[]).filter(
    (t) => !esPlantillaDeBelleza(t.rubros, slugPorCategoria.get(t.categoria_id)),
  );

  // 3. Filtro de rubro en cliente — más simple que armar el `.or()` con
  // sintaxis de contains en PostgREST (especialmente si el rubro tiene
  // caracteres raros). El dataset es chico, costo despreciable.
  const matchesRubro = (t: RawTemplate): boolean => {
    const rs = t.rubros || [];
    if (rs.length === 0) return true; // "para todos los rubros"
    return rubro ? rs.includes(rubro) : false;
  };
  const filtered = templates.filter(matchesRubro);

  // 4. Group by categoria_id. Dos agrupaciones: la del rubro del user y la de
  // TODAS las plantillas, que es de la que sale el relleno del punto 5.
  const agrupar = (lista: RawTemplate[]): Map<string, RawTemplate[]> => {
    const map = new Map<string, RawTemplate[]>();
    for (const t of lista) {
      const arr = map.get(t.categoria_id) || [];
      arr.push(t);
      map.set(t.categoria_id, arr);
    }
    return map;
  };
  const porRubro = agrupar(filtered);
  const todas = agrupar(templates);

  // 5. Mapear a CollectionV2[] respetando el orden de categorías. El mapeo por
  // fila vive en `mapearColeccion` (arriba), compartido con el fetch de Belleza.
  const mapear = mapearColeccion;

  // Primero las categorías del rubro del user.
  const collections: CollectionV2[] = [];
  for (const cat of categorias) {
    const tpls = porRubro.get(cat.id);
    if (!tpls || tpls.length === 0) continue; // categoría vacía → skip
    collections.push(mapear(cat, tpls));
  }

  // Si no llegan a `MIN_CATEGORIAS`, se completa con TODAS las que faltan —
  // no sólo hasta llegar a 3. Cortar en 3 dejaría a la home con exactamente
  // las filas que muestra y sin pozo, así que la rotación por refresh
  // (`useRotacionPlantillas`) sortearía siempre las mismas, que es justo lo
  // que no se quiere. `MIN_CATEGORIAS` decide SI se rellena, no hasta dónde.
  //
  // Van DESPUÉS de las del rubro y en el orden de `categorias`: la home rotea
  // sobre esta lista y "Ver todo" la muestra entera, así que lo más relevante
  // para el user queda arriba.
  if (collections.length < MIN_CATEGORIAS) {
    for (const cat of categorias) {
      if (porRubro.has(cat.id)) continue; // ya entró por rubro
      const tpls = todas.get(cat.id);
      if (!tpls || tpls.length === 0) continue;
      collections.push(mapear(cat, tpls));
    }
  }
  return collections;
}
